import { ChangeDetectionStrategy, Component, input, OnInit, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DialogFocusDirective } from './dialog-focus.directive';
import { DocumentRecordPickerComponent } from './document-record-picker.component';
import { tagName } from './documents-query';
import { DocTag, DocTarget } from './documents.models';

export type DocActionResult =
  | { kind: 'link'; target: DocTarget | null }
  | { kind: 'tags'; key: string; value: string; mode: 'set' | 'add' | 'clear' };

/** Actions on documents asking for a value: link to a record (or unlink), tag a selection */
@Component({
  selector: 'iu-document-action-dialog',
  standalone: true,
  imports: [TranslateModule, DocumentRecordPickerComponent, DialogFocusDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ad-backdrop" (click)="cancelled.emit()"></div>
    <section class="ad" role="dialog" aria-modal="true" iuDialogFocus [attr.aria-label]="title() | translate" (keydown.escape)="cancelled.emit()">
      <header class="ad-head">
        <h2>{{ title() | translate }} <span class="ad-muted">{{ 'documents.bulk.selected' | translate: { count: count() } }}</span></h2>
        <button type="button" class="ad-icon" (click)="cancelled.emit()" [attr.aria-label]="'documents.actions.close' | translate"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
      </header>
      <div class="ad-body">
        @if (kind() === 'link') {
          <iu-document-record-picker [value]="target()" (valueChange)="target.set($event)" />
          <p class="ad-muted">{{ 'documents.link.help' | translate }}</p>
        } @else {
          <div class="ad-row">
            <label class="ad-field">
              <span>{{ 'documents.bulk.tag' | translate }}</span>
              <!-- any tag name: the server adds the internal prefix -->
              <input class="p-inputtext ad-input" list="ad-tag-names" maxlength="60" [value]="key()" (input)="key.set($any($event.target).value)"
                [placeholder]="'documents.tagEditor.name' | translate" />
              <datalist id="ad-tag-names">
                @for (t of tagDefs(); track t.key) {
                  @if (t.editable) {
                    <option [value]="t.key">{{ t.key === 'DESCRIPTION' ? ('documents.tags.description' | translate) : t.key === 'KEYWORDS' ? ('documents.tags.keywords' | translate) : t.label }}</option>
                  }
                }
              </datalist>
            </label>
            <label class="ad-field">
              <span>{{ 'documents.bulk.mode' | translate }}</span>
              <select class="p-inputtext ad-input" (change)="mode.set($any($event.target).value)">
                <option value="add" [selected]="mode() === 'add'">{{ 'documents.bulk.modeAdd' | translate }}</option>
                <option value="set" [selected]="mode() === 'set'">{{ 'documents.bulk.modeSet' | translate }}</option>
                <option value="clear" [selected]="mode() === 'clear'">{{ 'documents.bulk.modeClear' | translate }}</option>
              </select>
            </label>
          </div>
          @if (mode() !== 'clear') {
            <label class="ad-field ad-field--wide">
              <span>{{ 'documents.bulk.value' | translate }}</span>
              <input class="p-inputtext ad-input" maxlength="1000" [value]="value()" (input)="value.set($any($event.target).value)" (keydown.enter)="confirm()" />
            </label>
          }
        }
      </div>
      <footer class="ad-foot">
        @if (kind() === 'link' && canUnlink()) {
          <button type="button" class="p-button p-button-secondary ad-btn" (click)="confirmed.emit({ kind: 'link', target: null })">{{ 'documents.link.unlink' | translate }}</button>
        }
        <span class="ad-spacer"></span>
        <button type="button" class="p-button p-button-secondary ad-btn" (click)="cancelled.emit()">{{ 'documents.actions.cancel' | translate }}</button>
        <button type="button" class="p-button p-button-primary ad-btn" [disabled]="!ready()" (click)="confirm()">{{ 'documents.actions.apply' | translate }}</button>
      </footer>
    </section>
  `,
  styles: `
    :host { position: absolute; inset: 0; z-index: 20; display: flex; align-items: center; justify-content: center; }
    .ad-backdrop { position: absolute; inset: 0; background: #0006; }
    .ad { position: relative; display: flex; flex-direction: column; width: min(520px, calc(100% - 32px)); max-height: calc(100% - 32px);
      border: 1px solid var(--fieldset-bd-color); border-radius: var(--global-border-radius); background: var(--fieldset-bg-color);
      color: var(--global-text-color-1); box-shadow: var(--global-shadow-box); }
    .ad-head, .ad-foot { display: flex; align-items: center; gap: 8px; padding: 10px 14px; }
    .ad-head { border-bottom: 1px solid var(--docs-bd-color); }
    .ad-head h2 { flex: 1 1 auto; margin: 0; font-size: 15px; }
    .ad-foot { border-top: 1px solid var(--docs-bd-color); flex-wrap: wrap; }
    @media (pointer: coarse) { .ad-input, .p-button.ad-btn, .ad-icon { min-height: 36px; } }
    .ad-body { flex: 1 1 auto; min-height: 0; overflow: auto; padding: 12px 14px; }
    .ad-row { display: flex; flex-wrap: wrap; gap: 8px 10px; }
    .ad-field { display: flex; flex-direction: column; gap: 3px; flex: 1 1 160px; font-size: 11px; color: var(--docs-ft-color-muted); }
    .ad-field--wide { margin-top: 8px; }
    .ad-input { height: 28px; padding: 0 8px; font: inherit; font-size: var(--font-size); color: var(--global-text-color-1); border: 1px solid var(--input-bd-color); border-radius: var(--docs-br); background: var(--input-bg-color); }
    .ad-muted { color: var(--docs-ft-color-muted); font-size: 11px; font-weight: normal; }
    .ad-spacer { flex: 1 1 auto; }
    .ad-icon { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border: none; background: transparent; color: inherit; cursor: pointer; border-radius: var(--docs-br); }
    .p-button.ad-btn { height: 28px; margin: 0; padding: 0 12px; font-size: var(--font-size); }
  `,
})
export class DocumentActionDialogComponent implements OnInit {
  readonly kind = input.required<'link' | 'tags'>();
  readonly count = input(1);
  readonly tagDefs = input<DocTag[]>([]);
  readonly initialTarget = input<DocTarget | null>(null);
  /** one of the documents is linked: unlink offered */
  readonly canUnlink = input(false);

  readonly confirmed = output<DocActionResult>();
  readonly cancelled = output<void>();

  readonly target = signal<DocTarget | null>(null);
  readonly key = signal('');
  readonly value = signal('');
  readonly mode = signal<'set' | 'add' | 'clear'>('add');

  ngOnInit(): void {
    this.target.set(this.initialTarget());
    const defs = this.tagDefs();
    this.key.set(defs.find((t) => t.key === 'KEYWORDS')?.key ?? '');
  }

  title(): string {
    return this.kind() === 'link' ? 'documents.link.title' : 'documents.bulk.tagsTitle';
  }

  ready(): boolean {
    if (this.kind() === 'link') return !!this.target();
    return /^[A-Z0-9_]{1,60}$/.test(tagName(this.key())) && (this.mode() === 'clear' || !!this.value().trim());
  }

  confirm(): void {
    if (!this.ready()) return;
    if (this.kind() === 'link') this.confirmed.emit({ kind: 'link', target: this.target() });
    else this.confirmed.emit({ kind: 'tags', key: tagName(this.key()), value: this.value().trim(), mode: this.mode() });
  }
}
